import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./SettingsTrialDropdown.scss";

import { ReactComponent as LogoutIcon } from "src/images/menu-icons/logout.svg";

import axios from "axios";
import classNames from "classnames";
import { useHistory } from "react-router";
import { Button } from "src/app/components/Button/Button";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { SettingsContext, SettingsTab } from "src/app/SettingsModal/enums";
import { openSettingsModal } from "src/app/SettingsModal/methods";
import {
  identify,
  setWorkspaceListDropdownVisible,
  getWorkspacesList,
} from "src/redux/main/mainActions";
import { API_URLS } from "src/utils/API_URLS";
import { AppLocation } from "src/redux/main/mainReducer";
import { showErrorToast } from "src/utils/methods";
import SubscriptionUsageModal, {
  SubscriptionUsageModalMode,
} from "src/app/subscription/modals/SubscriptionUsageModal/SubscriptionUsageModal";
import { WorkspacePermissionsEnum } from "src/utils/PermissionsEnums";
import { ReactComponent as AvatarIcon } from "../../../images/avatar.svg";
import { ReactComponent as SettingsAltIcon } from "../../../images/settings-alt.svg";
import { ReactComponent as SettingsIcon } from "../../../images/settings.svg";
import { ReactComponent as SharePlusIcon } from "../../../images/share-plus.svg";
import {
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../../components/Dropdown/Dropdown";
import WorkspacesList from "./WorkspacesList";
import { ReactComponent as DollarIcon } from "src/images/dollar.svg";
import { ReactComponent as LifebuoyIcon } from "src/images/lifebuoy.svg";
import { ReactComponent as LayoutIcon } from "src/images/layout.svg";
import { setSubscriptionContactModalDisplayMode } from "src/redux/subscription/subscriptionActions";
import ExternalLink from "src/app/components/ExternalLink/ExternalLink";

function SettingsTrialDropdown() {
  const [showStiggLimitReachedModal, setShowStiggLimitReachedModal] =
    useState(false);

  const {
    mainReducer: { activeWorkspaceUuid, identity },
  } = useSelector((state) => state);

  const { trialLimits } = identity || {};
  const { daysLimit } = trialLimits || {};

  const dispatch = useDispatch();
  const history = useHistory();

  const handleOpenSettingsModal = (context, tab) => {
    openSettingsModal(history, AppLocation.Navbar, context, tab);
  };

  const handleOpenSupportChat = () => {
    document.querySelector(".woot-widget-bubble")?.click();
  };

  useEffect(() => {
    dispatch(getWorkspacesList());
  }, [dispatch]);

  const logout = () => {
    axios
      .get(API_URLS.logout)
      .then(() => {
        dispatch(identify(activeWorkspaceUuid));
      })
      .catch((error) => {
        console.error(error);
        showErrorToast();
      });
  };

  const showTrial = trialLimits;

  return (
    <Dropdown
      className={classNames("settings-trial-dropdown", {
        "settings-trial-dropdown--trial-visible": showTrial,
      })}
      toRight
      onOpenChange={(open) => {
        // Keeps the redux flag in sync with the dropdown's real open state no matter how
        // it closes (menu item click, outside click, Escape, etc). A callback tied only to
        // one close path (e.g. outsideClickCallback) can leave this flag stuck at `true`
        // after the dropdown closes another way, which then permanently blocks the table's
        // Ctrl+F search shortcut until a full page reload resets redux state.
        dispatch(setWorkspaceListDropdownVisible(open));
      }}
    >
      {(isOpen, setIsOpen) => (
        <>
          {showTrial && (
            <div
              className={classNames("settings-trial-dropdown__button", {
                "settings-trial-dropdown__button--yellow":
                  daysLimit <= 2 && daysLimit > 0,
                "settings-trial-dropdown__button--red": daysLimit === 0,
              })}
              onClick={() => {
                setIsOpen((prev) => !prev);
              }}
            >
              {daysLimit === 1
                ? `${daysLimit} day left`
                : `${daysLimit} days left`}
            </div>
          )}

          <Button
            variant="light-grey"
            size="square-small"
            onClick={() => {
              setIsOpen((prev) => {
                if (!prev) {
                  document.dispatchEvent(
                    new CustomEvent("close-creator-search-settings"),
                  );
                }
                return !prev;
              });
            }}
            className="navbar__right-menu-icon"
          >
            <SettingsIcon />
          </Button>

          <DropdownMenu isOpen={isOpen}>
            <>
              <WorkspacesList />

              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  handleOpenSettingsModal(
                    SettingsContext.Member,
                    SettingsTab.Account,
                  );
                  setIsOpen(false);
                }}
                data-qa="My account"
              >
                <AvatarIcon />
                <IDHFormattedMessage
                  id="ws_my_account"
                  defaultMessage="My account"
                />
              </DropdownMenuItem>

              {identity?.permissions?.workspace?.includes(
                WorkspacePermissionsEnum.DISPLAY_MEMBERS_WORKSPACE_SETTINGS,
              ) && (
                <>
                  <DropdownMenuItem
                    onClick={() => {
                      handleOpenSettingsModal(
                        SettingsContext.Workspace,
                        SettingsTab.Members,
                      );
                      setIsOpen(false);
                    }}
                    data-qa="Invite"
                  >
                    <SharePlusIcon />
                    <IDHFormattedMessage
                      id="ws_invite"
                      defaultMessage="Invite"
                    />
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    onClick={() => {
                      handleOpenSettingsModal(
                        SettingsContext.Workspace,
                        SettingsTab.Members,
                      );
                      setIsOpen(false);
                    }}
                    data-qa="Workspace settings"
                  >
                    <SettingsAltIcon />
                    <IDHFormattedMessage
                      id="ws_workspace_settings"
                      defaultMessage="Workspace settings"
                    />
                  </DropdownMenuItem>
                </>
              )}
            </>

            {identity?.demoWsWorkspaceUrl && (
              <ExternalLink href={identity.demoWsWorkspaceUrl}>
                <DropdownMenuItem>
                  <LayoutIcon />
                  <IDHFormattedMessage
                    id="ws_demo_mode"
                    defaultMessage="Demo mode"
                  />
                </DropdownMenuItem>
              </ExternalLink>
            )}

            <DropdownMenuItem
              onClick={() => {
                handleOpenSupportChat();
                setIsOpen(false);
              }}
              data-qa="support-chat"
            >
              <LifebuoyIcon />
              <IDHFormattedMessage id="ws_support" defaultMessage="Support" />
            </DropdownMenuItem>

            {identity?.subscriptionData && (
              <DropdownMenuItem
                onClick={() => {
                  dispatch(setSubscriptionContactModalDisplayMode("default"));
                  setIsOpen(false);
                }}
                data-qa="contact-sales"
              >
                <DollarIcon />
                <IDHFormattedMessage
                  id="ws_contact_sales"
                  defaultMessage="Contact sales"
                />
              </DropdownMenuItem>
            )}

            <DropdownMenuItem onClick={logout} data-qa="logout-button">
              <LogoutIcon />
              <IDHFormattedMessage id="ws_logout" defaultMessage="Logout" />
            </DropdownMenuItem>
          </DropdownMenu>

          {showStiggLimitReachedModal && (
            <SubscriptionUsageModal
              onClose={() => setShowStiggLimitReachedModal(false)}
              mode={SubscriptionUsageModalMode.Info}
            />
          )}
        </>
      )}
    </Dropdown>
  );
}

export default SettingsTrialDropdown;
